<template>
  <div
    ref="rootEl"
    class="product-360 relative w-full h-full select-none touch-none cursor-grab active:cursor-grabbing bg-muted"
    role="img"
    :aria-label="alt || 'Vizualizare 360 grade'"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="endDrag"
    @pointercancel="endDrag"
    @pointerleave="endDrag"
  >
    <img
      v-for="(url, idx) in resolvedUrls"
      :key="`${url}-${idx}`"
      :src="url"
      :alt="idx === frameIndex ? alt || '' : ''"
      class="absolute inset-0 w-full h-full object-contain pointer-events-none"
      :class="idx === frameIndex ? 'opacity-100' : 'opacity-0'"
      draggable="false"
      :loading="idx === 0 ? 'eager' : 'lazy'"
    />

    <div
      class="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm"
    >
      <UIcon
        name="i-heroicons-arrow-path"
        class="h-3.5 w-3.5"
        :class="{ 'animate-spin': autoplaying }"
      />
      <span>{{ hint }}</span>
    </div>

    <div
      v-if="!ready"
      class="absolute inset-0 flex items-center justify-center bg-muted/80"
    >
      <UIcon
        name="i-heroicons-arrow-path"
        class="h-8 w-8 animate-spin text-muted"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    frames: string[];
    alt?: string;
    /** Pixels of horizontal drag per frame step */
    pxPerFrame?: number;
    /** Auto-advance frames until the user grabs */
    autoplay?: boolean;
    /** Frames per second while autoplaying */
    autoplayFps?: number;
  }>(),
  {
    alt: "",
    pxPerFrame: 12,
    autoplay: true,
    autoplayFps: 1,
  },
);

const { mediaUrl } = useMediaUrl();

const rootEl = ref<HTMLElement | null>(null);
const frameIndex = ref(0);
const ready = ref(false);
const dragging = ref(false);
const lastX = ref(0);
const accum = ref(0);
const autoplaying = ref(false);
let autoplayTimer: ReturnType<typeof setInterval> | null = null;
let resumeTimer: ReturnType<typeof setTimeout> | null = null;

const resolvedUrls = computed(() => props.frames.map((url) => mediaUrl(url)));

const hint = computed(() => {
  if (props.frames.length < 2) return "360°";
  if (dragging.value) return "Trage pentru a roti";
  if (autoplaying.value) return "360°";
  return "Trage pentru a roti";
});

watch(
  () => props.frames,
  () => {
    frameIndex.value = 0;
    ready.value = false;
    stopAutoplay();
    void preload();
  },
  { immediate: true },
);

watch(
  () => [props.autoplay, ready.value, props.frames.length] as const,
  () => {
    if (
      props.autoplay &&
      ready.value &&
      props.frames.length >= 2 &&
      !dragging.value
    ) {
      startAutoplay();
    } else if (!props.autoplay) {
      stopAutoplay();
    }
  },
);

onUnmounted(() => {
  stopAutoplay();
  clearResume();
});

async function preload() {
  const urls = resolvedUrls.value;
  if (!urls.length) {
    ready.value = true;
    return;
  }
  await Promise.all(
    urls.map(
      (src) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = src;
        }),
    ),
  );
  ready.value = true;
}

function step(delta: number) {
  const total = props.frames.length;
  if (total < 2) return;
  frameIndex.value = (((frameIndex.value + delta) % total) + total) % total;
}

function startAutoplay() {
  if (autoplayTimer || props.frames.length < 2) return;
  const ms = Math.max(40, Math.round(1000 / Math.max(1, props.autoplayFps)));
  autoplaying.value = true;
  autoplayTimer = setInterval(() => step(1), ms);
}

function stopAutoplay() {
  if (autoplayTimer) {
    clearInterval(autoplayTimer);
    autoplayTimer = null;
  }
  autoplaying.value = false;
}

function clearResume() {
  if (resumeTimer) {
    clearTimeout(resumeTimer);
    resumeTimer = null;
  }
}

function scheduleResume() {
  clearResume();
  if (!props.autoplay) return;
  resumeTimer = setTimeout(() => {
    if (!dragging.value) startAutoplay();
  }, 1800);
}

function onPointerDown(event: PointerEvent) {
  if (props.frames.length < 2) return;
  dragging.value = true;
  lastX.value = event.clientX;
  accum.value = 0;
  stopAutoplay();
  clearResume();
  rootEl.value?.setPointerCapture(event.pointerId);
}

function onPointerMove(event: PointerEvent) {
  if (!dragging.value) return;
  const dx = event.clientX - lastX.value;
  lastX.value = event.clientX;
  accum.value += dx;
  const threshold = props.pxPerFrame;
  while (accum.value <= -threshold) {
    step(1);
    accum.value += threshold;
  }
  while (accum.value >= threshold) {
    step(-1);
    accum.value -= threshold;
  }
}

function endDrag(event?: PointerEvent) {
  if (!dragging.value) return;
  dragging.value = false;
  if (event && rootEl.value?.hasPointerCapture(event.pointerId)) {
    rootEl.value.releasePointerCapture(event.pointerId);
  }
  scheduleResume();
}
</script>
